import { CompleteAccountModalContext, ModalContext, openRegisterForAuctionModal } from '@/redux/modules/modal';
import { EventContext } from '@/redux/modules/analytics';
import { fetchItemFacetsIfNeeded } from '@/redux/modules/itemFacets';
import { fetchLAPaymentCard, getHasValidCardOnFile } from '@/redux/modules/creditCard';
import { fetchLiveCatalogStatusIfNeeded } from '@/redux/modules/liveCatalogStatus';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getBidderId, getUserIsInGoodStanding, isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { getBidderSubmittedBid, submitLiveBid } from '@/redux/modules/liveBid';
import { getBidLimitBalance } from '@/redux/modules/bidLimit';
import {
    getCatalogCurrency,
    getCatalogIsAutopay,
    getCatalogIsInstantAutopay,
    getCatalogIsLAPayments,
    getCatalogIsTimed,
    getCatalogPaymentProvider,
    getCatalogSellerId,
    getCatalogSellerIsJasper52,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import {
    getCatalogNetworkStatus,
    getLatestMessageTimestamp,
    getLiveActiveBidder,
    getLiveBidderOutbid,
    getLiveCatalogClosed,
    getLiveCatalogCurrentItemId,
    getLiveCatalogStatus,
    getLiveCatalogWon,
    getLiveItemDataCurrentAskPrice,
    getLiveItemDataLeadingBidder,
} from '@/redux/modules/console';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { getConsoleBranding } from '@/redux/modules/consoleBranding';
import { getCountDown } from '@/redux/modules/bidCountDown';
import { getDeskSupportLink, isItemPage } from '@/utils/urls';
import { getIsTimedPlus, getItemSummary } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getIsWhiteLabel, getWhiteLabelPrimaryColor } from '@/redux/modules/whiteLabel';
import { getLocale } from '@/redux/modules/intl';
import { getUiHasLoggedInBefore } from '@/redux/modules/autoPopModal/autoPopModal.selectors';
import { HoverWrapper, StyledButton, StyledLink } from './styles';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTrackBidPlaced } from '@/hooks/kameleoon/tracking/useTrackBidPlaced';
import { useTrackBidPlacedOnItemPage } from '@/hooks/kameleoon/tracking/useTrackBidPlacedOnItemPage';
import { useTrackJ52BidPlaced } from '@/hooks/kameleoon/tracking/useTrackJ52BidPlaced';
import {
    useTrackPlaceBidPressedAnalytics,
    useTrackRegisterForAuctionPressedAnalytics,
} from '@/utils/amplitude/analytics';
import classNames from 'classnames';
import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import ms from 'ms';
import theme from '@liveauctioneers/caterwaul-components/lib/theme/theme';

type Props = {
    biddingLocked?: boolean;
    catalogId: number;
    itemId: number;
    noLinks?: boolean;
};

const BiddingConsoleButton = ({ biddingLocked, catalogId, itemId, noLinks }: Props) => {
    const dispatch = useAppDispatch();

    const trackRegisterForAuctionPressedAnalytics = useTrackRegisterForAuctionPressedAnalytics();

    const userLoggedIn = useAppSelector((state) => isUserLoggedIn(state));
    const accountInGoodStanding = useAppSelector((state) => getUserIsInGoodStanding(state));
    const bidLimitBalance = useAppSelector((state) => getBidLimitBalance(state, catalogId));
    const { approved, declined, limited, pending, suspended } = useAppSelector((state) =>
        getCatalogRegistration(state, catalogId)
    );
    const { bidderHasBid } = useAppSelector((state) => getItemSummary(state, itemId));
    const showItemPageVariant = false;

    const activeBidder = useAppSelector((state) => getLiveActiveBidder(state, catalogId));
    const bidderOutbid = useAppSelector((state) => getLiveBidderOutbid(state, catalogId));
    const closed = useAppSelector((state) => getLiveCatalogClosed(state, catalogId));
    const currentItem = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const networkStatus = useAppSelector((state) => getCatalogNetworkStatus(state, catalogId));
    const liveCatalogStatus = useAppSelector((state) => getLiveCatalogStatus(state, catalogId));
    const won = useAppSelector((state) => getLiveCatalogWon(state, catalogId));

    const isWhiteLabel = useAppSelector(getIsWhiteLabel);
    const whiteLabelPrimaryColor = useAppSelector(getWhiteLabelPrimaryColor);
    const paymentProvider = useAppSelector((state) => getCatalogPaymentProvider(state, catalogId));
    const isAutopay = useAppSelector((state) => getCatalogIsAutopay(state, catalogId));
    const currency = useAppSelector((state) => getCatalogCurrency(state, catalogId));
    const isInstantAutopay = useAppSelector((state) => getCatalogIsInstantAutopay(state, catalogId));
    const isTimed = useAppSelector((state) => getCatalogIsTimed(state, catalogId));
    const isTimedPlus = useAppSelector((state) => getIsTimedPlus(state, itemId));
    const houseId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const isLAPayments = useAppSelector((state) => getCatalogIsLAPayments(state, catalogId));
    const hasCreditCard: boolean = useAppSelector((state) => getHasValidCardOnFile(state, paymentProvider));

    const { color: consoleBrandingColor } = useAppSelector((state) => getConsoleBranding(state, catalogId, houseId));
    const timeStamp = useAppSelector((state) => getCountDown(state, itemId));
    const hasLoggedInBefore = useAppSelector(getUiHasLoggedInBefore);
    const liveUpdateTimestamp = useAppSelector((state) => getLatestMessageTimestamp(state, catalogId));
    const locale = useAppSelector(getLocale);
    const bidderId = useAppSelector(getBidderId);
    const currentAskPrice = useAppSelector((state) => getLiveItemDataCurrentAskPrice(state, itemId));
    const leadingBidder = useAppSelector((state) => getLiveItemDataLeadingBidder(state, itemId));
    const readyToBid = useAppSelector((state) => getCatalogNetworkStatus(state, catalogId));
    const submitted = useAppSelector(getBidderSubmittedBid);

    const trackBidPlaced = useTrackBidPlaced();
    const trackPlaceBidPressed = useTrackPlaceBidPressedAnalytics(catalogId, itemId);

    // Kameleoon Jasper52 BuyersPremium experiment
    const trackJ52BidPlacedConversion = useTrackJ52BidPlaced();
    const isJasper52 = useAppSelector((state) => getCatalogSellerIsJasper52(state, catalogId));

    const location = useLocation();
    const isOnItemPage = isItemPage(location.pathname);

    const myBid = bidderId === leadingBidder;
    const showCountDown = isTimed && Boolean(timeStamp) && approved;

    // Kameleoon ItemPage BidCanvas experiment
    const trackBidPlacedOnItemPage = useTrackBidPlacedOnItemPage();

    const [, setTime] = useState(0);

    useEffect(() => {
        // bump ts to re-render the app at least once every 5 seconds
        const interval = setInterval(() => setTime(Date.now()), 5000);
        return () => {
            clearInterval(interval);
        };
    }, []);

    useEffect(() => {
        dispatch(fetchLAPaymentCard());
    }, [dispatch]);

    const checkReadyToBid = useCallback(() => {
        const timeSinceLastMessage = Date.now() - liveUpdateTimestamp;

        if (timeSinceLastMessage > ms('4m') && Boolean(catalogId)) {
            dispatch(fetchLiveCatalogStatusIfNeeded(catalogId, houseId, true));
        }

        return (
            readyToBid &&
            itemId === currentItem &&
            itemId > 0 &&
            networkStatus === 'connected' &&
            liveCatalogStatus === 'live' &&
            !closed &&
            !won &&
            Boolean(currentAskPrice) &&
            catalogId > 0 &&
            timeSinceLastMessage < ms('5m')
        );
    }, [
        catalogId,
        closed,
        currentAskPrice,
        currentItem,
        dispatch,
        houseId,
        itemId,
        liveCatalogStatus,
        liveUpdateTimestamp,
        networkStatus,
        readyToBid,
        won,
    ]);

    const [isReadyToPlace, setIsReadyToPlace] = useState(false);

    useEffect(() => {
        if (isReadyToPlace && !submitted) {
            setIsReadyToPlace(false);
            trackPlaceBidPressed();
            // don't only rely on the "disabled" button to prevent bidders who haven't been approved
            if (accountInGoodStanding && (approved || limited) && !biddingLocked && checkReadyToBid() && !submitted) {
                dispatch(submitLiveBid(Date.now(), catalogId, currentAskPrice, currency, itemId, location));
                if (!bidderHasBid) {
                    trackBidPlaced(currentAskPrice);

                    if (isJasper52) {
                        trackJ52BidPlacedConversion(currentAskPrice);
                    }
                }
                if (!bidderHasBid && isOnItemPage) {
                    trackBidPlacedOnItemPage(currentAskPrice);
                }
            }
        }
    }, [
        accountInGoodStanding,
        approved,
        bidderHasBid,
        biddingLocked,
        catalogId,
        checkReadyToBid,
        currency,
        currentAskPrice,
        dispatch,
        isJasper52,
        isOnItemPage,
        isReadyToPlace,
        itemId,
        limited,
        location,
        submitted,
        trackBidPlaced,
        trackBidPlacedOnItemPage,
        trackJ52BidPlacedConversion,
        trackPlaceBidPressed,
    ]);

    const placeBid = useCallback(async () => {
        await dispatch(fetchItemFacetsIfNeeded([itemId]));
        setIsReadyToPlace(true);
    }, [dispatch, itemId]);

    const modalContext = useMemo<ModalContext>(
        () => ({
            catalogId,
            eventContext: EventContext.registerForAuction,
            id: catalogId,
            type: CompleteAccountModalContext.RegisterForAuction,
        }),
        [catalogId]
    );

    const registerForCatalog = useCallback(async () => {
        trackRegisterForAuctionPressedAnalytics();

        dispatch(
            openRegisterForAuctionModal({
                catalogId,
                fromBiddingConsole: true,
                isInstantAutopay,
                itemId,
                modalContext,
            })
        );
    }, [catalogId, dispatch, isInstantAutopay, itemId, modalContext, trackRegisterForAuctionPressedAnalytics]);

    const auctionPaused = liveCatalogStatus === 'paused';

    // ensure that we don't enable the button unless we have the required info
    const ready = checkReadyToBid();
    const wouldExceedBidLimit = limited && currentAskPrice > bidLimitBalance;

    const formattedAsk = (
        <FormattedCurrency
            className="currency"
            key="bidding-console-button-formatted-ask"
            noFraction
            sourceCurrency={currency}
            value={currentAskPrice}
        />
    );

    const bidMessage = (
        <FormattedMessage
            id="bidding_console.console.button.bid"
            key="bidding-console-button-bid-message"
            values={{ amount: formattedAsk }}
        />
    );

    const outbidMessage = (
        <FormattedMessage
            id="bidding_console.console.button.outbid"
            key="bidding-console-button-outbid-message"
            values={{ amount: formattedAsk }}
        />
    );

    const brandedButtonStyle =
        consoleBrandingColor !== ''
            ? {
                  background: consoleBrandingColor,
                  backgroundColor: consoleBrandingColor,
                  borderBottom: consoleBrandingColor,
                  borderColor: consoleBrandingColor,
                  borderTop: consoleBrandingColor,
              }
            : {};

    if (!userLoggedIn) {
        const loginTextId = hasLoggedInBefore
            ? 'bidding_console.console.button.login'
            : 'bidding_console.console.button.signup';

        return (
            <StyledButton
                className="tertiary"
                locale={locale}
                onClick={registerForCatalog}
                primary
                red={!isWhiteLabel || whiteLabelPrimaryColor === theme.colors.blue200}
                style={brandedButtonStyle}
            >
                <FormattedMessage id={loginTextId} />
            </StyledButton>
        );
    } else if (isLAPayments && isAutopay && !hasCreditCard) {
        return (
            <StyledButton
                className="primary"
                locale={locale}
                onClick={registerForCatalog}
                primary
                style={brandedButtonStyle}
            >
                <FormattedMessage id="bidding_console.console.button.addPaymentMethod" />
            </StyledButton>
        );
    } else if (pending) {
        return (
            <StyledButton
                className="primary"
                disabled
                locale={locale}
                primary
                style={brandedButtonStyle}
            >
                <FormattedMessage id="bidding_console.console.button.pending" />
            </StyledButton>
        );
    } else if (declined) {
        const classes = classNames('tertiary', 'submitted');
        const buttonText = <FormattedMessage id="bidding_console.console.button.declined" />;
        return noLinks ? (
            <StyledButton
                className={classes}
                disabled
                locale={locale}
                primary
                red={!isWhiteLabel || whiteLabelPrimaryColor === theme.colors.blue200}
            >
                {buttonText}
            </StyledButton>
        ) : (
            <StyledLink
                locale={locale}
                to={getDeskSupportLink()}
            >
                {buttonText}
            </StyledLink>
        );
    } else if (suspended || !accountInGoodStanding) {
        const classes = classNames('tertiary', 'submitted');
        const buttonText = <FormattedMessage id="bidding_console.console.button.suspended" />;
        return noLinks ? (
            <StyledButton
                className={classes}
                disabled
                locale={locale}
                primary
                red={!isWhiteLabel || whiteLabelPrimaryColor === theme.colors.blue200}
            >
                {buttonText}
            </StyledButton>
        ) : (
            <StyledLink
                locale={locale}
                to={getDeskSupportLink()}
            >
                {buttonText}
            </StyledLink>
        );
    } else if (limited || approved) {
        if (closed) {
            const classes = classNames('tertiary', 'submitted', {
                inactive: !activeBidder,
                winning: won,
            });
            return (
                <StyledButton
                    className={classes}
                    disabled
                    locale={locale}
                    primary
                    red={!isWhiteLabel || whiteLabelPrimaryColor === theme.colors.blue200}
                >
                    <FormattedMessage
                        id={won ? 'bidding_console.console.button.won' : 'bidding_console.console.button.closed'}
                    />
                </StyledButton>
            );
        } else if (myBid && !isTimedPlus) {
            // Timed+ auctions show this button on the item page, and we want to allow users to change bid amount
            return (
                <StyledButton
                    className="winning"
                    locale={locale}
                    primary
                >
                    <FormattedMessage id="bidding_console.console.button.winning" />
                </StyledButton>
            );
        } else {
            const classes = classNames('tertiary', {
                submitted: submitted,
            });

            const hoverClasses = classNames('hover-wrapper', {
                countdown: showCountDown && !submitted,
            });

            return (
                <HoverWrapper className={hoverClasses}>
                    <StyledButton
                        className={classes}
                        disabled={submitted || !ready || wouldExceedBidLimit || biddingLocked || auctionPaused}
                        locale={locale}
                        onClick={placeBid}
                        primary
                        red={!isWhiteLabel || whiteLabelPrimaryColor === theme.colors.blue200}
                    >
                        {submitted ? (
                            <FormattedMessage
                                id="bidding_console.console.button.sent"
                                values={{ amount: formattedAsk }}
                            />
                        ) : bidderOutbid ? (
                            outbidMessage
                        ) : (
                            bidMessage
                        )}
                    </StyledButton>
                </HoverWrapper>
            );
        }
    } else {
        const classes = classNames('primary', {
            primaryVariant: showItemPageVariant,
        });

        return (
            <StyledButton
                className={classes}
                locale={locale}
                onClick={registerForCatalog}
                primary
                style={brandedButtonStyle}
            >
                <FormattedMessage id="bidding_console.console.button.register_long" />
            </StyledButton>
        );
    }
};

export default BiddingConsoleButton;
